import { useContext, type ReactNode } from 'react';

import styles from './installation-dialog.module.css';
import { useNavbarIconUrl } from '@shared/lib/hooks';
import { AppContext } from '@/app.context';
import {
	Dialog,
	DialogContent,
	dialogCardBackdropClassName,
	dialogCardClassName,
} from '@shared/ui/dialog';
import { cn } from '@/lib/utils';
import { Share } from './installation';
import { T, useT } from '@shared/i18n';

/**
 * Moved out of `views/home/Home.tsx` (improvements/07-template-architecture.md's
 * `views/` sort, user directive 2026-08-16: "if code is shared make it one
 * level up and import it") -- confirmed shared across 7+ templates plus
 * `poets` itself, not poets-exclusive like the rest of `Home.tsx` was.
 *
 * Note: this dialog's CSS (`InstallationDialog.module.css`, extracted
 * verbatim from `views/home/home.module.css`) hardcodes poets' own brand
 * fonts (`Domine-Regular`, `CrimsonText-Regular`). That's not a regression
 * introduced by this move -- every non-poets template already rendered this
 * exact dialog with this exact CSS before the move, since they all imported
 * the same component from `views/home/home.tsx`. Flagged here as a
 * pre-existing cross-tenant styling leak, not fixed as part of this move.
 */
export const InstallationDialog = ({
	show,
	setShowInstallation,
}: InstallationDialogProps) => {
	const t = useT();
	const { device } = useContext(AppContext);
	const navbarIconUrl = useNavbarIconUrl();
	const message = device?.isTouchable
		? t('install.safari.addToHomeScreen')
		: t('install.safari.addToDock');
	const bold = (chunks: ReactNode) => <b>{chunks}</b>;
	const br = () => <br />;
	return (
		<Dialog
			open={show}
			onOpenChange={(open) => {
				if (!open) {
					setShowInstallation(false);
				}
			}}
		>
			<DialogContent
				className={cn(
					dialogCardClassName,
					styles['installation-dialog-container'],
				)}
				backdropClassName={dialogCardBackdropClassName}
			>
				<div className={styles['installation-dialog']}>
					<img src={navbarIconUrl} alt={t('common.logo')} />
					<h3>{t('common.installApp')}</h3>
					<p>
						<T k="install.intro" components={{ br }} />
					</p>
					{isSafari() ? (
						<ol>
							<li>
								<T
									k="install.safari.share"
									components={{ icon: () => <Share /> }}
								/>
							</li>
							<li>
								<T
									k="install.safari.select"
									values={{ option: message }}
									components={{ b: bold }}
								/>
							</li>
							<li>
								<T k="install.safari.add" components={{ b: bold }} />
							</li>
						</ol>
					) : (
						<ol>
							<li>
								<T k="install.other.menu" components={{ br }} />
							</li>
							<li>
								<T k="install.other.select" components={{ b: bold }} />
							</li>
							<li>
								<T k="install.other.install" components={{ b: bold }} />
							</li>
						</ol>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export interface InstallationDialogProps {
	show: boolean;
	setShowInstallation: (l: boolean) => void;
}

export function isSafari() {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
